import { type DocumentSummary } from '../../services/api/documents';

export type BulkReplaceFile = { name: string; text: string };

export type BulkReplaceReady = { name: string; id: string; text: string };
export type BulkReplaceSkipped = { name: string; reason: string };

export type BulkReplacePlan = {
  ready: BulkReplaceReady[];
  skipped: BulkReplaceSkipped[];
};

// doc-sync skriver id:t i frontmatter; utan det vet vi inte vilket Supabase-dokument
// filen hör till, och den får inte ersätta något på måfå.
export const parseFrontmatterId = (content: string): string | null => {
  const match = content.match(/^---\s*([\s\S]*?)---/);
  if (!match) return null;
  const idLine = match[1].split(/\r?\n/).find((line) => /^\s*id\s*:/.test(line));
  if (!idLine) return null;
  const value = idLine.split(':').slice(1).join(':').trim();
  return value.replace(/^['"](.*)['"]$/, '$1') || null;
};

export const planBulkReplace = (
  files: BulkReplaceFile[],
  documents: DocumentSummary[],
): BulkReplacePlan => {
  const knownIds = new Set(documents.map((doc) => String(doc.id)));
  const ready: BulkReplaceReady[] = [];
  const skipped: BulkReplaceSkipped[] = [];

  for (const file of files) {
    const id = parseFrontmatterId(file.text);
    if (!id) {
      skipped.push({ name: file.name, reason: 'ingen id i frontmatter' });
      continue;
    }
    if (!knownIds.has(id)) {
      skipped.push({ name: file.name, reason: `id=${id} saknas i Supabase` });
      continue;
    }
    ready.push({ name: file.name, id, text: file.text });
  }

  return { ready, skipped };
};

export type BulkReplaceResult = {
  message: string;
  isError: boolean;
};

export const summarizeBulkReplace = ({
  replaced,
  skipped,
  failed,
  syncError,
}: {
  replaced: number;
  skipped: string[];
  failed: string[];
  syncError?: string;
}): BulkReplaceResult => {
  const summary = `Bulk-ersätt klar: ${replaced} ersatta, ${skipped.length} hoppade över, ${failed.length} fel.`;

  // Utan synk ligger texterna i Supabase men boten svarar ur de gamla dokumenten.
  // Det får aldrig se ut som att allt gick vägen.
  const syncLine = syncError
    ? `\nInnehållet är sparat men kunskapsbasen uppdaterades inte: ${syncError}. Tryck "Synka docs nu".`
    : '';

  if (failed.length > 0 || syncError) {
    const details = failed.length > 0 ? `\n${failed.join('\n')}` : '';
    return { message: `${summary}${details}${syncLine}`, isError: true };
  }
  if (skipped.length > 0) {
    return { message: `${summary}\nHoppade över:\n${skipped.join('\n')}`, isError: false };
  }
  return { message: summary, isError: false };
};
